import { useAuth } from '../auth';
import { produce } from 'immer';
import React, { useState, useEffect, useRef } from 'react';
import { useSocket, SocketUser, EventNames, CareSignalSocket } from '.';

interface PatientSocketContextValue {
  enterPatientRoom: (patientId: number) => void;
  leavePatientRoom: (patientId: number) => void;
  getPatientRoomMembers: (patientId: number) => SocketUser[];
  isCurrentUserInRoom: (patientId: number) => boolean;
}

interface Listener {
  eventName: any;
  handler: any;
}

interface UpdateRoomsFn {
  (users: SocketUser[], patientId: number): void;
}

interface InitExistingRoomsFn {
  (existingRooms: PatientRooms): void;
}

const PatientSocketContext = React.createContext<PatientSocketContextValue>({
  enterPatientRoom: null,
  leavePatientRoom: null,
  getPatientRoomMembers: null,
  isCurrentUserInRoom: null
});

const initRoomsHandler = (initRooms: InitExistingRoomsFn) => {
  return (roomsData: PatientRooms) => {
    initRooms(roomsData);
  };
};

const listHandler = (updateRooms: UpdateRoomsFn) => {
  return (users: SocketUser[], patientId: number) => {
    updateRooms(users, patientId);
  };
};

const getListHandler = (socket: CareSignalSocket) => {
  return (_user: SocketUser, patientId: number) => {
    socket.emit(EventNames.LIST, patientId);
  };
};

const processListeners = (
  action: 'on' | 'off',
  socket: CareSignalSocket,
  updateRooms: UpdateRoomsFn,
  initRooms: InitExistingRoomsFn
) => {
  const listeners: Listener[] = [
    { eventName: EventNames.LIST_RESULT, handler: listHandler(updateRooms) },
    { eventName: EventNames.ENTER, handler: getListHandler(socket) },
    {
      eventName: EventNames.ENTER_ACCOUNT_SUCCESS,
      handler: initRoomsHandler(initRooms)
    },
    { eventName: EventNames.LEAVE, handler: getListHandler(socket) }
  ];
  for (const listener of listeners) {
    socket[action](listener.eventName, listener.handler);
  }
};

interface PatientRooms {
  [patientId: number]: SocketUser[];
}

export const PatientSocketProvider: React.FC = (props) => {
  /**
   * Adding this ref and then using it within the component
   * allows us to track when the component unmounts and then
   * react accordingly.
   *
   * This allows us to avoid warnings about memory leaks
   * in the browser dev console.
   */
  const isMountedRef = useRef<boolean>(true);
  const { user } = useAuth();
  const socket = useSocket();
  const [patientRooms, setPatientRooms] = useState<PatientRooms>(null);

  const initRooms = (existingRooms: PatientRooms) => {
    if (isMountedRef && isMountedRef.current === false) {
      return;
    }
    setPatientRooms(existingRooms);
  };

  const updateRooms: UpdateRoomsFn = (users, patientId) => {
    if (isMountedRef && isMountedRef.current === false) {
      return;
    }
    setPatientRooms(
      produce((rooms: PatientRooms) => {
        if (!rooms) {
          rooms = {};
        }
        if (!rooms[patientId]) {
          rooms[patientId] = [];
        }
        rooms[patientId] = users;
        return rooms;
      })
    );
  };

  const getPatientRoomMembers = (
    patientId: number,
    withCurrentUser = false
  ) => {
    if (!patientRooms) {
      return;
    }
    if (!patientRooms[patientId]) {
      return;
    }
    if (withCurrentUser) {
      return patientRooms[patientId];
    }
    return patientRooms[patientId].filter(
      (member) => member.email !== user.email
    );
  };

  const isCurrentUserInRoom = (patientId: number) => {
    const members = getPatientRoomMembers(patientId, true);
    if (!members || !members.length) {
      return false;
    }
    const socketUser = members.find(
      (socketUser) => socketUser.email === user.email
    );
    if (socketUser) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (socket) {
      isMountedRef.current = true;
      processListeners('on', socket, updateRooms, initRooms);
      socket.emit(EventNames.ENTER_ACCOUNT);
    }
    return () => {
      isMountedRef.current = false;
      if (socket) {
        processListeners('off', socket, updateRooms, initRooms);
        socket.emit(EventNames.LEAVE_ACCOUNT);
      }
    };
  }, [socket]);

  const enterPatientRoom = (patientId: number) => {
    socket.emit(EventNames.ENTER, patientId);
  };

  const leavePatientRoom = (patientId: number) => {
    socket.emit(EventNames.LEAVE, patientId);
  };

  const value: PatientSocketContextValue = {
    enterPatientRoom,
    leavePatientRoom,
    getPatientRoomMembers,
    isCurrentUserInRoom
  };

  return <PatientSocketContext.Provider value={value} {...props} />;
};

export function usePatientSocket(): PatientSocketContextValue {
  const context = React.useContext(PatientSocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a PatientSocketProvider');
  }
  return context;
}
